import { SessionInfo } from "../../../api/models";

/**
 * Parses query parameters from URL
 */
export const parseQueryParams = (queryValue: string): { [key: string]: string } => {
    const queryParameter = new URLSearchParams(queryValue);
    const params: { [key: string]: string } = {};

    for (const [key, value] of queryParameter.entries()) {
        params[key] = value;
    }

    return params;
};

/**
 * Updates session storage for search functionality
 */
export const updateSessionStorageForSearch = (
    action: "shift" | "remove",
    activeSession: SessionInfo,
    currentIndex: string,
    lastActiveIndexRef: number,
    backupState: {
        todaySessions: SessionInfo[];
        last7Sessions: SessionInfo[];
        last30Sessions: SessionInfo[];
        monthlySessions: any[];
    },
    setBackupState: {
        setBackupTodaySessions: (sessions: SessionInfo[]) => void;
        setBackupLast7Sessions: (sessions: SessionInfo[]) => void;
        setBackupLast30Sessions: (sessions: SessionInfo[]) => void;
        setBackupMonthlySessions: (sessions: any[]) => void;
    }
): void => {
    const { todaySessions, last7Sessions, last30Sessions, monthlySessions } = backupState;
    const { setBackupTodaySessions, setBackupLast7Sessions, setBackupLast30Sessions, setBackupMonthlySessions } = setBackupState;

    if (action === "shift") {
        setBackupTodaySessions([activeSession, ...todaySessions]);
        return;
    }

    if (action === "remove") {
        const MONTHLY_SESSION_INDEX = 4;
        const sessionBuckets: any = {
            1: { state: todaySessions, setState: setBackupTodaySessions },
            2: { state: last7Sessions, setState: setBackupLast7Sessions },
            3: { state: last30Sessions, setState: setBackupLast30Sessions }
        };

        if (lastActiveIndexRef === MONTHLY_SESSION_INDEX) {
            setBackupMonthlySessions(
                monthlySessions.map(yearGroup => ({
                    ...yearGroup,
                    sessions: yearGroup.sessions.map((sessionGroup: any) => ({
                        ...sessionGroup,
                        records: sessionGroup.records.filter((s: any) => s.session_id !== currentIndex)
                    }))
                }))
            );
            return;
        }

        const bucket: any = sessionBuckets[lastActiveIndexRef];
        if (!bucket) return;

        const { state, setState } = bucket;
        setState(state.filter((s: any) => s.session_id !== currentIndex));
    }
};

/**
 * Checks if disclaimer should be shown
 */
export const shouldShowDisclaimer = (): boolean => {
    const status = localStorage.getItem("disclaimerHide");
    return status === "initial";
};

/**
 * Updates disclaimer status
 */
export const updateDisclaimerStatus = (): void => {
    const status = localStorage.getItem("disclaimerHide") ?? "initial";
    if (status === "initial") {
        localStorage.setItem("disclaimerHide", "active");
    }
};

/**
 * Resolves locale key used in appInfo.product_categories.
 * Falls back to normalized/language match and finally to first available locale.
 */
export const resolveProductLocale = (selectedLocale: string, appInfo: any): string => {
    const productCategories = appInfo?.product_categories;
    const localeKeys = productCategories ? Object.keys(productCategories) : [];

    if (!productCategories || localeKeys.length === 0) {
        return selectedLocale;
    }

    if (productCategories[selectedLocale]) {
        return selectedLocale;
    }

    const normalizeLocale = (locale: string): string => locale.toLowerCase().replace(/_/g, "-");
    const normalizedSelected = normalizeLocale(selectedLocale);

    const normalizedMatch = localeKeys.find(localeKey => normalizeLocale(localeKey) === normalizedSelected);
    if (normalizedMatch) {
        return normalizedMatch;
    }

    const selectedLanguage = normalizedSelected.split("-")[0];
    const languageMatch = localeKeys.find(localeKey => normalizeLocale(localeKey).split("-")[0] === selectedLanguage);
    if (languageMatch) {
        return languageMatch;
    }

    return localeKeys[0];
};

/**
 * Checks if input box should be disabled
 */
export const shouldDisableInputBox = (selectedLocale: string, appInfo: any): boolean => {
    const validProducts = appInfo?.product_categories?.[selectedLocale]?.filter((item: any) => item.text !== "ALL");
    return Boolean(validProducts?.length);
};
